// ─────────────────────────────────────────────────────────────
// TradeLog
// Extracted from App.js.
// ─────────────────────────────────────────────────────────────

import { useState } from "react";
import { Card } from "../../components/ui/Card";
import { Pill } from "../../components/ui/Pill";
import { Tag } from "../../components/ui/Tag";
import { C } from "../../lib/colors";
import { isSessionShifted } from "../../lib/format";
import { TradeShareModal } from "../landing/TradeShareModal";

export function TradeLog({ 
  trades, 
  deleteTrade, 
  updateTrade, 
  setView, 
  openTradeReview,
  accounts = [],
  currentAccountId,
  setCurrentAccountId,
  isDemoAccount = false,
}) {
  const [filter, setFilter] = useState("all");
  const [shareTarget, setShareTarget] = useState(null);

  // Filter by selected account
  const accountTrades = currentAccountId 
    ? trades.filter(t => t.account_id === currentAccountId) 
    : trades;

  // Broker-accurate sort: exited_at ISO timestamp (ms precision) when available,
  // falling back to real calendar date + exit_time for manually-entered trades.
  const brokerSort = (a, b) => {
    if (a.exited_at && b.exited_at) {
      const diff = new Date(b.exited_at) - new Date(a.exited_at);
      if (diff !== 0) return diff;
      const bidA = Number(a.broker_trade_id) || 0;
      const bidB = Number(b.broker_trade_id) || 0;
      return bidA - bidB;
    }
    const realDate = (t) => {
      const s = t.trade_date || t.created_at?.split("T")[0] || "";
      const h = parseInt((t.exit_time || t.entry_time || "00").slice(0, 2), 10);
      if (s && !isNaN(h) && h >= 17) {
        const d = new Date(s + "T12:00:00"); d.setDate(d.getDate() - 1);
        return d.toISOString().slice(0, 10);
      }
      return s;
    };
    const dA = `${realDate(a)} ${a.exit_time || a.entry_time || "00:00"}`;
    const dB = `${realDate(b)} ${b.exit_time || b.entry_time || "00:00"}`;
    if (dB !== dA) return dB.localeCompare(dA);
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  };

  const sortedAccountTrades = [...accountTrades].sort(brokerSort);

  const filtered = filter === "all"
    ? sortedAccountTrades
    : sortedAccountTrades.filter(t =>
        (filter === "Win" || filter === "Loss") ? t.result === filter
        : (t.setup_type === filter || t.entry_signal === filter)
      );

  return (
    <div>
      <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 800 }}>Trade Journal</h2>
      <p style={{ color: C.muted, fontSize: 12, marginBottom: 20 }}>{filtered.length} trades logged</p>

      {isDemoAccount && (
        <div style={{
          padding: "10px 14px", marginBottom: 16,
          background: "#1a1500", border: "1px solid #f5a62330",
          borderRadius: 8, fontSize: 12, color: "#f5a623",
        }}>
          👀 Demo mode — trade data is read-only. Sign up to log and manage your own trades.
        </div>
      )}
      {accounts.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: C.muted, marginBottom: 6, display: "block" }}>Viewing Account</label>
          <select 
            value={currentAccountId || ""} 
            onChange={e => setCurrentAccountId(e.target.value)}
            style={{ width: "100%", padding: 12, background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }}
          >
            {accounts.map(acc => (
              <option key={acc.id} value={acc.id}>
                {acc.name} — {acc.account_type?.toUpperCase()} (${acc.starting_balance})
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {["all", "Win", "Loss"].map(f => (
          <button 
            key={f} 
            onClick={() => setFilter(f)}
            style={{ 
              padding: "6px 14px", 
              borderRadius: 20, 
              border: `1px solid ${filter === f ? C.blue : C.border}`, 
              background: filter === f ? C.blue + "20" : "transparent", 
              color: filter === f ? C.blue : C.muted, 
              cursor: "pointer", 
              fontSize: 12, 
              fontWeight: 600 
            }}
          >
            {f === "all" ? "All Trades" : f}
          </button>
        ))}
      </div>

      <Card style={{ padding: 0 }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["Date", "Symbol", "Dir", "Setup", "Entry", "Exit", "P&L", "Score", ""].map(h => (
                  <th key={h} style={{ padding: "12px 14px", textAlign: "left", color: C.muted, fontWeight: 500, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr 
                  key={t.id} 
                  style={{ 
                    borderBottom: `1px solid ${C.border}15`, 
                    cursor: "pointer",
                    transition: "background 0.1s"
                  }}
                  onClick={() => openTradeReview(t)}
                  onMouseEnter={e => e.currentTarget.style.background = "#ffffff05"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  {/* Your existing table row content */}
                  <td style={{ padding: "11px 14px", color: C.muted, fontFamily: "monospace", whiteSpace: "nowrap" }}>
                    {t.trade_date?.slice(5)}
                    {isSessionShifted(t) && (
                      <span
                        title={`Session day. This trade was entered the evening before (after 6 PM ET), but per CME session conventions it belongs to ${t.trade_date}.`}
                        style={{ marginLeft: 4, opacity: 0.7 }}
                      >
                        🌙
                      </span>
                    )}
                    <br />
                    <span style={{ fontSize: 10 }}>{(t.entry_time || t.trade_time || "")?.slice(0, 8)}</span>
                  </td>
                  <td style={{ padding: "11px 14px", color: C.text, fontFamily: "monospace", fontWeight: 700 }}>{t.symbol}</td>
                  <td style={{ padding: "11px 14px", color: t.direction === "Long" ? C.green : C.red, fontWeight: 700 }}>
                    {t.direction === "Long" ? "↑" : "↓"}
                  </td>
                  <td style={{ padding: "11px 14px" }}>
                    <Tag label={t.setup_type || "—"} color={C.purple} />
                  </td>
                  <td style={{ padding: "11px 14px", fontFamily: "monospace", color: C.sub }}>{t.entry_price || "—"}</td>
                  <td style={{ padding: "11px 14px", fontFamily: "monospace", color: C.sub }}>{t.exit_price || "—"}</td>
                  <td style={{ padding: "11px 14px" }}>
                    <Pill value={t.pnl || 0} />
                  </td>
                  <td style={{ 
                    padding: "11px 14px", 
                    color: !t.discipline_score ? C.muted : 
                           t.discipline_score >= 8 ? C.green : 
                           t.discipline_score >= 6 ? C.yellow : C.red, 
                    fontFamily: "monospace" 
                  }}>
                    {t.discipline_score ? `${t.discipline_score}/10` : "—"}
                  </td>
                  <td style={{ padding: "11px 14px" }}>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setShareTarget(t); }}
                      title="Share trade"
                      style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, padding: "2px 6px", borderRadius: 4 }}
                      onMouseEnter={e => e.currentTarget.style.color = "#4caf7d"}
                      onMouseLeave={e => e.currentTarget.style.color = C.muted}
                    >
                      ↑
                    </button>
                    <button 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        if (isDemoAccount) return;
                        if (window.confirm('Delete this trade permanently?')) deleteTrade(t.id); 
                      }}
                      title={isDemoAccount ? "Not available in demo" : "Delete trade"}
                      style={{ 
                        background: "none", 
                        border: "none", 
                        color: isDemoAccount ? C.border : C.muted, 
                        cursor: isDemoAccount ? "not-allowed" : "pointer", 
                        fontSize: 14, 
                        padding: "2px 6px", 
                        borderRadius: 4 
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = C.red}
                      onMouseLeave={e => e.currentTarget.style.color = C.muted}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {shareTarget && (
        <TradeShareModal
          trade={shareTarget}
          allTrades={accountTrades}
          onClose={() => setShareTarget(null)}
        />
      )}
    </div>
  );
}










// ─────────────────────────────────────────────────────────────
// RESET PASSWORD SCREEN (from email recovery link)
// ─────────────────────────────────────────────────────────────
